import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { api, type Order, type OrderFilters } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import {
  EMPTY,
  etaDeltaMinutes,
  formatMinutes,
  formatTime,
  minutesUntil,
} from '../shared/lib/format';
import { statusLabel, toneForOrder, type Tone } from '../shared/lib/status';
import { useAsync } from '../shared/lib/use-async';
import { useLocationNames } from '../shared/lib/use-location-names';
import { useDismiss } from '../shared/lib/use-dismiss';
import { useShortcuts } from '../shared/lib/use-shortcuts';
import { periodFilters, periodFromParams, periodToParams, type Period } from '../shared/lib/period';
import { PeriodPicker } from '../shared/ui/PeriodPicker';
import {
  EmptyState,
  FilterChips,
  pillStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../shared/ui/primitives';
import { OrderDrawer } from './OrderDrawer';

/**
 * Поток заказов — главный экран (разд. 4, FR-571).
 *
 * Устроен как лента доставок панели Zenta: заголовок со счётчиком найденного, поиск
 * по мере ввода, фильтры-пилюли и по карточке на доставку — статус крупно, под ним
 * время (прибытие, срок, опоздание) и полоса этапов. Проблемная карточка — в рамке.
 *
 * Фильтры отвечают на два разных вопроса, и смешивать их нельзя: «есть ли проблема» —
 * одна пилюля «Требуют внимания», «на каком этапе» — «Новые», «В работе»,
 * «Завершённые ▾». Раньше двенадцать пилюль пересекались, и один заказ попадал в три
 * сразу. Редкие конечные исходы спрятаны в выпадающий список «Завершённые».
 *
 * Период — пилюля «Сегодня» с календарём, как в панели: по умолчанию сегодняшние
 * заказы, «Всё время» снимает ограничение. Период действует на ленту и на все счётчики.
 * Ручной ввод и импорт ушли из шапки в меню «Настройки»: это аварийные каналы (A3),
 * а не ежедневные действия.
 *
 * Обновление — опросом раз в 15 секунд (FR-573). Список при этом не переставляется
 * под курсором: сортировка задана сервером, а новые заказы приходят сверху.
 *
 * Клавиатура (разд. 15): `/` и `⌘K` — поиск, `J`/`K` — движение по списку, `Enter` —
 * открыть карточку, `Esc` — закрыть. Диспетчер открывает за смену сотни карточек, и
 * мышь здесь не выбор, а потеря времени.
 */

const REFRESH_MS = 15_000;
const SEARCH_DEBOUNCE_MS = 350;

type ChipCode =
  'all' | 'attention' | 'new' | 'active' | 'done' | 'cancelled' | 'returned' | 'deferred';

/** Пилюли первого ряда. */
const CHIPS: readonly { code: ChipCode; tone?: 'danger' }[] = [
  { code: 'all' },
  { code: 'attention', tone: 'danger' },
  { code: 'new' },
  { code: 'active' },
];

/** Конечные исходы — в выпадающем списке «Завершённые»; первый — по умолчанию. */
const FINISHED_CHIPS: readonly ChipCode[] = ['done', 'cancelled', 'returned', 'deferred'];

const ALL_CHIPS: readonly ChipCode[] = [...CHIPS.map(({ code }) => code), ...FINISHED_CHIPS];

/**
 * Код фильтра из адреса. Старые фильтры проблем (`at_risk`, `unassigned`, `triage`,
 * `goods`) вошли в «Требуют внимания» — ссылки и закладки на них ведут туда.
 */
export function normalizeChip(value: string | null): ChipCode {
  if (value === 'at_risk' || value === 'unassigned' || value === 'triage' || value === 'goods') {
    return 'attention';
  }

  return ALL_CHIPS.includes(value as ChipCode) ? (value as ChipCode) : 'all';
}

/**
 * Фильтр периода для пилюли. «Требуют внимания» — сигнал, а не история: заказ, вчера
 * застрявший без курьера, сегодня требует действия не меньше, поэтому период к ней не
 * применяется. Иначе утром счётчик показывает 0, а в «Разборе» заказ висит сутки.
 */
function periodFor(code: ChipCode, period: Period | null): ReturnType<typeof periodFilters> {
  return code === 'attention' ? {} : periodFilters(period);
}

const PERIOD_STORAGE_KEY = 'tms.flow.period';

/** Последний выбранный период этого браузера; хранилище недоступно — `null`. */
function storedPeriod(): Record<string, string | undefined> | null {
  try {
    const raw = window.localStorage.getItem(PERIOD_STORAGE_KEY);

    return raw === null ? null : (JSON.parse(raw) as Record<string, string | undefined>);
  } catch {
    return null;
  }
}

function storePeriod(value: Record<string, string | undefined>): void {
  try {
    window.localStorage.setItem(PERIOD_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Приватное окно или запрет хранилища: период просто не запомнится.
  }
}

export function FlowPage({
  params,
  onParams,
}: {
  readonly params: URLSearchParams;
  readonly onParams: (next: Record<string, string | undefined>) => void;
}): ReactElement {
  const messages = useMessages();
  const locationName = useLocationNames();
  const chip = normalizeChip(params.get('chip'));
  const search = params.get('q') ?? '';
  const selected = params.get('order');
  const period = periodFromParams(params, new Date());
  const periodKey = JSON.stringify(periodFilters(period));
  const periodInAddress = params.has('period') || params.has('from');

  /*
   * Переход в «Доставки» из меню приходит без периода, и лента молча возвращалась к
   * «Сегодня». Выбранный период запоминается в браузере и восстанавливается, если
   * в адресе периода нет; ссылка с явным периодом важнее запомненного.
   */
  useEffect(() => {
    if (!periodInAddress) {
      const stored = storedPeriod();

      if (stored !== null && (stored['period'] !== undefined || stored['from'] !== undefined)) {
        onParams(stored);
      }
    }
  }, [periodInAddress]);

  const [limit, setLimit] = useState(50);
  // Курсор клавиатуры живёт отдельно от открытой карточки: по списку ходят, не
  // открывая каждую строку, иначе `J` превращается в череду запросов.
  const [cursor, setCursor] = useState<string | null>(null);
  const [typed, setTyped] = useState(search);

  /*
   * Поиск — по мере ввода, как в панели доставки: Enter не нужен. Пауза перед
   * запросом — чтобы не слать запрос на каждую букву номера, который диктуют.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = typed.trim();

      if (next !== search) {
        onParams({ q: next === '' ? undefined : next });
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [typed]);

  // Номер и телефон ищет сервер; адрес, получателя и курьера — загруженный список.
  const serverQuery = isNumberQuery(search) ? search : '';

  const filters = useMemo<OrderFilters>(
    () => ({ ...filtersFor(chip, serverQuery, limit), ...periodFor(chip, period) }),
    [chip, serverQuery, limit, periodKey],
  );

  const orders = useAsync(() => api.orders(filters), [JSON.stringify(filters)], {
    intervalMs: REFRESH_MS,
  });

  /*
   * Счётчики фильтров.
   *
   * Берутся общим числом по каждому фильтру (`total`), а не подсчётом загруженной
   * страницы: страница — это первые 50 строк, и «Выполненные (50)» при тысяче
   * выполненных заказов было бы не счётчиком, а совпадением. Запрос идёт с `limit: 1`:
   * нужны не строки, а число.
   */
  const counters = useAsync(
    async () => {
      const totals = await Promise.all(
        ALL_CHIPS.map(
          async (code) =>
            (await api.orders({ ...filtersFor(code, serverQuery, 1), ...periodFor(code, period) }))
              .total ?? 0,
        ),
      );

      return new Map<ChipCode, number>(ALL_CHIPS.map((code, index) => [code, totals[index] ?? 0]));
    },
    [serverQuery, periodKey],
    { intervalMs: REFRESH_MS },
  );

  const loaded = orders.data?.data ?? [];
  const rows = serverQuery === '' && search !== '' ? matchText(loaded, search) : loaded;
  const found =
    serverQuery === '' && search !== '' ? rows.length : (orders.data?.total ?? rows.length);
  const searchRef = useRef<HTMLInputElement>(null);

  const step = (delta: number): void => {
    if (rows.length === 0) {
      return;
    }

    const current = rows.findIndex((order) => order.id === selected);
    // Из «ничего не выбрано» `J` встаёт на первую строку, а `K` — на последнюю:
    // движение вниз начинается сверху, вверх — снизу.
    const next = current < 0 ? (delta > 0 ? 0 : rows.length - 1) : current + delta;
    const target = rows[Math.min(Math.max(next, 0), rows.length - 1)];

    if (target?.id !== undefined) {
      setCursor(target.id);
    }
  };

  useShortcuts({
    onSearch: () => searchRef.current?.focus(),
    onNext: () => step(1),
    onPrevious: () => step(-1),
    onOpen: () => {
      if (cursor !== null) {
        onParams({ order: cursor });
      }
    },
    onClose: () => {
      if (selected !== null) {
        onParams({ order: undefined });
      }
    },
  });

  const open = (order: Order): void => {
    setCursor(order.id ?? null);
    onParams({ order: order.id });
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <h1>{messages.cabinet.flow.title}</h1>
        <span className="caption">
          {`${messages.cabinet.flow.found} `}
          <b className="num" style={{ color: 'var(--text-2)' }}>
            {found}
          </b>
        </span>
      </header>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          height: 'var(--field-h)',
          padding: '0 var(--sp-3)',
          background: 'var(--surface)',
          border: '1px solid var(--line-strong)',
          borderRadius: 'var(--r-sm)',
          color: 'var(--text-muted)',
          maxWidth: '560px',
        }}
      >
        <SearchIcon />
        <input
          ref={searchRef}
          type="search"
          aria-label={messages.cabinet.flow.searchPlaceholder}
          placeholder={messages.cabinet.flow.searchPlaceholder}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          style={{
            border: 'none',
            background: 'none',
            boxShadow: 'none',
            padding: 0,
            minHeight: 0,
            flex: 1,
            minWidth: 0,
          }}
        />
      </label>

      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center' }}>
        <PeriodPicker
          value={period}
          onChange={(next) => {
            const value = periodToParams(next, new Date());

            storePeriod(value);
            onParams(value);
          }}
        />
        <FilterChips
          active={chip}
          onSelect={(code) => onParams({ chip: code === 'all' ? undefined : code })}
          items={CHIPS.map(({ code, tone }) => ({
            code,
            label: messages.cabinet.flow.chips[code],
            count: counters.data?.get(code) ?? 0,
            ...(tone === undefined ? {} : { tone }),
          }))}
        />
        <FinishedPicker
          active={chip}
          counts={counters.data ?? new Map()}
          onSelect={(code) => onParams({ chip: code })}
        />
      </div>

      {orders.error === null ? null : (
        <div
          role="status"
          style={{
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--r-md)',
            border: '1px solid var(--danger-line)',
            background: 'var(--danger-soft)',
            color: 'var(--danger)',
          }}
        >
          {`${messages.cabinet.common.error}: ${orders.error.code}`}
        </div>
      )}

      {rows.length === 0 ? (
        <section
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-lg)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <EmptyState
            text={
              chip === 'all' && search === ''
                ? messages.cabinet.common.empty
                : messages.cabinet.common.emptyFiltered
            }
            {...(chip === 'all' && search === ''
              ? {}
              : {
                  onReset: () => {
                    setTyped('');
                    onParams({ chip: undefined, q: undefined });
                  },
                  resetLabel: messages.cabinet.common.reset,
                })}
          />
        </section>
      ) : (
        <div role="list" style={{ display: 'grid', gap: 'var(--sp-3)' }}>
          <div aria-hidden style={{ ...ROW_GRID, padding: '0 var(--sp-5)' }} className="caption">
            <span>{messages.cabinet.flow.columnNumber}</span>
            <span>{messages.cabinet.flow.columnDepartment}</span>
            <span>{messages.cabinet.flow.columnStatus}</span>
            <span>{messages.cabinet.flow.columnAmount}</span>
            <span>{messages.cabinet.flow.columnClient}</span>
            <span>{messages.cabinet.flow.columnCourier}</span>
            <span />
          </div>

          {rows.map((order) => (
            <DeliveryRow
              key={order.id}
              order={order}
              pickup={locationName(order.pickup_location_id) ?? null}
              selected={order.id === (selected ?? cursor)}
              onOpen={() => open(order)}
            />
          ))}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        <span className="num">{`${messages.cabinet.flow.shown} ${rows.length}`}</span>
        {loaded.length >= limit ? (
          <button
            type="button"
            style={secondaryButtonStyle}
            onClick={() => setLimit((value) => value + 50)}
          >
            {messages.cabinet.flow.more}
          </button>
        ) : null}
      </div>

      {selected === null ? null : (
        <OrderDrawer
          orderId={selected}
          onClose={() => onParams({ order: undefined })}
          onChanged={orders.reload}
        />
      )}
    </div>
  );
}

/**
 * Колонки ленты: заказ, отдел, статус, сумма, клиент, курьер, кнопка. Одна сетка у
 * шапки и у каждой карточки — колонки совпадают без таблицы.
 */
/*
 * Колонки сжимаются вместе с окном: при ширине около 1100 px лента помещается без
 * горизонтальной прокрутки, и «подробнее» остаётся внутри карточки. Узкие колонки —
 * номер, отдел, сумма — фиксированы; текстовые делят остаток.
 */
const ROW_GRID = {
  display: 'grid',
  gridTemplateColumns:
    '120px 128px minmax(170px, 1.4fr) 72px minmax(150px, 1.5fr) minmax(110px, 1fr) auto',
  gap: 'var(--sp-3)',
  alignItems: 'center',
} as const;

/**
 * Этапы доставки для полосы под статусом. Сколько этапов пройдено — по статусу;
 * минуты на каждом этапе появятся, когда в заказе будут отметки времени этапов.
 */
const STAGE_KEYS = ['accepted', 'assigned', 'pickedUp', 'inTransit', 'delivered'] as const;

const STAGE_REACHED: Record<string, number> = {
  new: 1,
  unassigned: 1,
  assigned: 2,
  picked_up: 3,
  in_transit: 4,
  delivered: 5,
};

/**
 * Одна доставка — отдельная карточка, как в ленте доставок панели Zenta: номер и
 * время, отдел, статус крупно с цветной строкой времени и полосой этапов, сумма,
 * клиент с адресом, курьер. Проблемная — с цветной рамкой: её видно, не читая строку.
 */
function DeliveryRow({
  order,
  pickup,
  selected,
  onOpen,
}: {
  readonly order: Order;
  readonly pickup: string | null;
  readonly selected: boolean;
  readonly onOpen: () => void;
}): ReactElement {
  const messages = useMessages();
  const tone = railTone(order);
  const problem = tone === 'danger' || tone === 'warn';
  const frame = tone === 'danger' ? 'var(--danger-rail)' : 'var(--warning-rail)';
  const status = order.status ?? 'new';
  const reached = STAGE_REACHED[status];
  const line = timeLine(order, messages);
  const slot = deliveryWindow(order);

  return (
    <article
      role="listitem"
      onClick={onOpen}
      style={{
        ...ROW_GRID,
        padding: 'var(--sp-4) var(--sp-5)',
        background: selected ? 'var(--accent-soft)' : 'var(--surface)',
        border: `1px solid ${problem ? frame : 'var(--line)'}`,
        boxShadow: problem
          ? `inset var(--rail-w) 0 0 ${frame}, var(--shadow-card)`
          : 'var(--shadow-card)',
        borderRadius: 'var(--r-lg)',
        cursor: 'pointer',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {order.language === undefined ? null : (
            <span
              style={{
                fontSize: 'var(--fs-xs)',
                fontWeight: 'var(--fw-bold)',
                color: 'var(--text-muted)',
                border: '1px solid var(--line-strong)',
                borderRadius: 'var(--r-xs)',
                padding: '0 var(--sp-1)',
              }}
            >
              {order.language.toUpperCase()}
            </span>
          )}
          <b className="num" style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-bold)' }}>
            {order.display_id}
          </b>
        </div>
        {slot === null ? null : (
          <span
            className="sub num"
            style={{ color: 'var(--warning)', fontWeight: 'var(--fw-semibold)' }}
          >
            {slot}
          </span>
        )}
        <span className="sub num">
          {[order.external_number, formatTime(order.created_at)]
            .filter((part) => part !== null && part !== undefined && part !== EMPTY)
            .join(' · ')}
        </span>
      </div>

      <div style={{ minWidth: 0 }}>
        <span
          style={{
            display: 'inline-block',
            padding: 'var(--sp-1) var(--sp-3)',
            borderRadius: 'var(--r-pill)',
            fontSize: 'var(--fs-xs)',
            fontWeight: 'var(--fw-bold)',
            lineHeight: 1.25,
            background:
              order.mode === 'distribution' ? 'var(--warning-soft)' : 'var(--accent-soft)',
            color: order.mode === 'distribution' ? 'var(--warning)' : 'var(--accent-ink)',
          }}
        >
          {order.mode === undefined ? EMPTY : messages.cabinet.modes[order.mode]}
        </span>
        <span className="sub" style={ellipsis}>
          {pickup ?? EMPTY}
        </span>
      </div>

      <div style={{ minWidth: 0 }}>
        <b style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-bold)' }}>
          {statusLabel(messages, status)}
        </b>
        {order.goods_location === 'courier' ? (
          <span
            className="sub"
            style={{ color: 'var(--warning)', fontWeight: 'var(--fw-semibold)' }}
          >
            {messages.status.goodsWithCourier}
          </span>
        ) : null}
        <span
          className="sub num"
          style={{
            fontWeight: 'var(--fw-semibold)',
            color:
              line.tone === 'danger'
                ? 'var(--danger)'
                : line.tone === 'ok'
                  ? 'var(--success)'
                  : 'var(--text-muted)',
          }}
        >
          {line.text}
        </span>

        <div
          style={{ display: 'flex', gap: 'var(--sp-1)', marginTop: 'var(--sp-2)' }}
          aria-label={messages.cabinet.flow.stagesLabel}
        >
          {STAGE_KEYS.map((key, index) => {
            const done = reached !== undefined && index < reached;

            return (
              <span
                key={key}
                title={messages.cabinet.flow.stages[key]}
                style={{
                  width: 'var(--stage-w)',
                  height: 'var(--stage-h)',
                  borderRadius: 'var(--r-pill)',
                  background:
                    reached === undefined
                      ? 'var(--danger-line)'
                      : done
                        ? 'var(--accent)'
                        : 'var(--line)',
                }}
              />
            );
          })}
        </div>
      </div>

      <div className="num">
        <b style={{ fontWeight: 'var(--fw-bold)' }}>
          {order.amount === undefined ? EMPTY : formatAmount(order.amount)}
        </b>
        <span className="sub">{messages.cabinet.flow.currency}</span>
      </div>

      <div style={{ minWidth: 0 }}>
        <b style={{ fontWeight: 'var(--fw-bold)' }}>{order.recipient_name ?? EMPTY}</b>
        <span className="sub">{order.delivery_address?.raw ?? EMPTY}</span>
      </div>

      <div style={{ minWidth: 0 }}>
        {order.driver?.name === undefined || order.driver?.name === null ? (
          <span className="caption">{messages.cabinet.flow.noDriver}</span>
        ) : (
          <b style={{ fontWeight: 'var(--fw-bold)' }}>{order.driver.name}</b>
        )}
      </div>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
        style={{
          ...primaryButtonStyle,
          borderRadius: 'var(--r-pill)',
          padding: '0 var(--sp-4)',
        }}
      >
        {messages.cabinet.flow.details}
      </button>
    </article>
  );
}

/**
 * Строка времени под статусом — то, ради чего смотрят ленту: успевает ли заказ.
 * Живой: «просрочен на N мин» или «прибытие 14:20 · срок 14:30» (с опозданием —
 * красным). Завершённый: какой был срок.
 */
function timeLine(
  order: Order,
  messages: ReturnType<typeof useMessages>,
): { text: string; tone: 'danger' | 'ok' | 'muted' } {
  const due = `${messages.cabinet.flow.columnDue.toLocaleLowerCase()} ${formatTime(order.deadline_at)}`;
  const late = lateness(order, messages);

  if (!isActive(order)) {
    return { text: due, tone: order.status === 'delivered' ? 'ok' : 'muted' };
  }

  if (order.eta_delivery_at === null || order.eta_delivery_at === undefined) {
    return late === null
      ? { text: due, tone: 'muted' }
      : { text: `${late} · ${due}`, tone: 'danger' };
  }

  const eta = `${messages.cabinet.flow.etaAt} ${formatTime(order.eta_delivery_at)}`;

  return late === null
    ? { text: `${eta} · ${due}`, tone: 'muted' }
    : { text: `${eta} · ${late}`, tone: 'danger' };
}

/** Окно доставки «к 09:00–10:00» — дистрибуция возит в окно, а не к дедлайну. */
function deliveryWindow(order: Order): string | null {
  if (order.delivery_window_from === null || order.delivery_window_from === undefined) {
    return order.delivery_window_to === null || order.delivery_window_to === undefined
      ? null
      : `≤ ${formatTime(order.delivery_window_to)}`;
  }

  return `${formatTime(order.delivery_window_from)}–${formatTime(order.delivery_window_to)}`;
}

function formatAmount(amount: number): string {
  return Math.round(amount).toLocaleString('ru-RU');
}

/**
 * «Завершённые ▾»: выполненные, отменённые, возвращённые, отложенные — одной пилюлей со
 * списком. Счётчик пилюли — сумма исходов, в списке — у каждого свой.
 */
function FinishedPicker({
  active,
  counts,
  onSelect,
}: {
  readonly active: ChipCode;
  readonly counts: ReadonlyMap<ChipCode, number>;
  readonly onSelect: (code: ChipCode) => void;
}): ReactElement {
  const messages = useMessages();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const selected = FINISHED_CHIPS.includes(active);
  const total = FINISHED_CHIPS.reduce((sum, code) => sum + (counts.get(code) ?? 0), 0);

  useDismiss(open, close, root);

  return (
    <div ref={root} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-pressed={selected}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        style={{
          ...pillStyle,
          background: selected ? 'var(--accent)' : 'var(--surface)',
          borderColor: selected ? 'var(--accent)' : 'var(--line)',
          color: selected ? 'var(--text-on-accent)' : 'var(--text-2)',
          opacity: !selected && total === 0 ? 'var(--muted-opacity)' : 1,
        }}
      >
        {`${selected ? messages.cabinet.flow.chips[active] : messages.cabinet.flow.chips.finished} ${selected ? (counts.get(active) ?? 0) : total} ▾`}
      </button>

      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--sp-1))',
            left: 0,
            minWidth: '220px',
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-pop)',
            padding: 'var(--sp-2)',
            display: 'grid',
            gap: 'var(--sp-1)',
            zIndex: 20,
          }}
        >
          {FINISHED_CHIPS.map((code) => (
            <button
              key={code}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSelect(code);
              }}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 'var(--sp-4)',
                textAlign: 'left',
                border: 'none',
                borderRadius: 'var(--r-sm)',
                padding: 'var(--sp-2) var(--sp-3)',
                fontSize: 'var(--fs)',
                background: code === active ? 'var(--accent-soft)' : 'transparent',
                color: code === active ? 'var(--accent-ink)' : 'var(--text-2)',
              }}
            >
              <span>{messages.cabinet.flow.chips[code]}</span>
              <b className="num">{counts.get(code) ?? 0}</b>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const FINISHED = new Set([
  'delivered',
  'cancelled',
  'returned',
  'resolved_cancelled',
  'resolved_returned',
]);

function isActive(order: Order): boolean {
  return !FINISHED.has(order.status ?? 'new');
}

/**
 * Полоска слева у строки: опасное и рискованное видно в списке раньше, чем в колонке
 * статуса. Риск важнее стадии, но только у живого заказа — у доставленного он ничего
 * не значит.
 */
export function railTone(order: Order): Tone {
  if (order.at_risk === true && isActive(order)) {
    return 'warn';
  }

  return toneForOrder(order.status ?? 'new', order.goods_location);
}

/**
 * Опоздание — только у живого заказа: «просрочен на» после дедлайна, «опаздывает на»
 * по прогнозу. У доставленного минуты опоздания — история, а не сигнал.
 */
function lateness(order: Order, messages: ReturnType<typeof useMessages>): string | null {
  if (!isActive(order)) {
    return null;
  }

  const left = minutesUntil(order.deadline_at);

  if (left !== null && left < 0) {
    return `${messages.cabinet.flow.overdueBy} ${formatMinutes(-left, { minutes: messages.common.minutesShort, hours: messages.common.hoursShort })}`;
  }

  const delta = etaDeltaMinutes(order.eta_delivery_at, order.deadline_at);

  if (delta !== null && delta > 0) {
    return `${messages.cabinet.flow.lateBy} ${formatMinutes(delta, { minutes: messages.common.minutesShort, hours: messages.common.hoursShort })}`;
  }

  return null;
}

/**
 * Номер заказа или телефон — ищет сервер (FR-587.3): «A-00087», «ORD-1000», «148820»,
 * «+998 90…». Всё остальное — адрес, получатель, курьер — ищется в загруженном списке.
 */
export function isNumberQuery(query: string): boolean {
  return /^\p{L}{0,4}-?[\d\s+()-]*\d[\d\s+()-]*$/u.test(query.trim());
}

function matchText(rows: readonly Order[], query: string): Order[] {
  const needle = query.trim().toLocaleLowerCase();

  return rows.filter((order) =>
    [order.delivery_address?.raw, order.recipient_name, order.driver?.name].some((value) =>
      (value ?? '').toLocaleLowerCase().includes(needle),
    ),
  );
}

function SearchIcon(): ReactElement {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: 'none' }}
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

const ellipsis = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

/**
 * Чип → фильтр запроса.
 *
 * Вынесено функцией, а не собрано в разметке: это правило поведения экрана, и его
 * проверяет тест — расхождение чипа и фильтра диспетчер заметит не сразу, а на
 * пропущенном заказе.
 */
export function filtersFor(chip: ChipCode, search: string, limit: number): OrderFilters {
  const base: OrderFilters = { limit, ...(search === '' ? {} : { q: search }) };

  switch (chip) {
    case 'attention':
      return { ...base, attention: true };
    case 'new':
      return { ...base, status: 'new' };
    case 'active':
      // Три статуса одним чипом: для диспетчера «назначен», «забрал» и «в пути» —
      // одно состояние «едет», и различает их колонка статуса, а не фильтр.
      return { ...base, status: 'assigned,picked_up,in_transit' };
    case 'done':
      return { ...base, status: 'delivered' };
    case 'deferred':
      // Отложенные — перенесённые решением по недоставке (FR-515): срок сдвинут,
      // заказ ждёт следующей попытки.
      return { ...base, status: 'resolved_rescheduled' };
    case 'returned':
      return { ...base, status: 'returned' };
    case 'cancelled':
      // Отмена бывает командой CT и решением по разбору — для списка это одно.
      return { ...base, status: 'cancelled,resolved_cancelled' };
    default:
      return base;
  }
}
